import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { settingsApi, footerApi } from '../lib/api';

interface FooterContent {
  id: number;
  section: string;
  title: string | null;
  content: string;
  order: number;
  isActive: boolean;
}

interface FooterLink {
  text: string;
  url: string;
}

export function Footer() {
  const { data: settings } = useQuery({
    queryKey: ['public-settings'],
    queryFn: () => settingsApi.getPublic().then((res) => res.data),
    staleTime: 0, // Always refetch
    refetchOnMount: true,
  });

  const { data: footerContents } = useQuery({
    queryKey: ['footer-contents'],
    queryFn: () => footerApi.getContents().then((res) => res.data),
    staleTime: 0,
    refetchOnMount: true,
  });

  const getContentBySection = (section: string): FooterContent | undefined => {
    return footerContents?.find((c: FooterContent) => c.section === section);
  };

  const parseFooterContent = (content: string): FooterLink[] | null => {
    try {
      const parsed = JSON.parse(content);
      if (Array.isArray(parsed.links)) {
        return parsed.links;
      }
      return null;
    } catch {
      return null;
    }
  };

  const companyContent = getContentBySection('company');
  const companyDescriptionContent = getContentBySection('company-description');
  const quickLinksContent = getContentBySection('quickLinks');
  const servicesContent = getContentBySection('services');
  const contactContent = getContentBySection('contact');
  const copyrightContent = getContentBySection('copyright');

  const companyLinks = companyContent ? parseFooterContent(companyContent.content) : null;
  const quickLinks = quickLinksContent ? parseFooterContent(quickLinksContent.content) : null;
  const serviceLinks = servicesContent ? parseFooterContent(servicesContent.content) : null;
  const contactLinks = contactContent ? parseFooterContent(contactContent.content) : null;

  return (
    <footer className="bg-primary-900 text-white mt-auto">
      <div className="container-custom py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Section */}
          <div>
            <h3 className="text-xl font-bold mb-4">
              {companyContent?.title || settings?.company?.name || import.meta.env.VITE_APP_NAME || 'KUMBİLGİ'}
            </h3>
            {companyLinks ? (
              <ul className="space-y-2 text-gray-400">
                {companyLinks.map((link, index) => (
                  <li key={index}>
                    {link.url.startsWith('http') ? (
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-white transition-colors"
                      >
                        {link.text}
                      </a>
                    ) : (
                      <Link to={link.url} className="hover:text-white transition-colors">
                        {link.text}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-400">
                {companyDescriptionContent?.content || 'Teknoloji çözümlerinde yılların deneyimi.'}
              </p>
            )}
          </div>

          {/* Quick Links Section */}
          <div>
            {quickLinksContent?.title ? (
              <h4 className="font-semibold mb-4">{quickLinksContent.title}</h4>
            ) : (
              <h4 className="font-semibold mb-4">Hızlı Linkler</h4>
            )}
            {quickLinks ? (
              <ul className="space-y-2 text-gray-400">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    {link.url.startsWith('http') ? (
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-white transition-colors"
                      >
                        {link.text}
                      </a>
                    ) : (
                      <Link to={link.url} className="hover:text-white transition-colors">
                        {link.text}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/" className="hover:text-white transition-colors">
                    Ana Sayfa
                  </Link>
                </li>
                <li>
                  <Link
                    to="/hakkimizda"
                    className="hover:text-white transition-colors"
                  >
                    Hakkımızda
                  </Link>
                </li>
                <li>
                  <Link
                    to="/cozumler"
                    className="hover:text-white transition-colors"
                  >
                    Çözümler
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="hover:text-white transition-colors">
                    Blog
                  </Link>
                </li>
              </ul>
            )}
          </div>

          {/* Services Section */}
          <div>
            {servicesContent?.title ? (
              <h4 className="font-semibold mb-4">{servicesContent.title}</h4>
            ) : (
              <h4 className="font-semibold mb-4">Hizmetler</h4>
            )}
            {serviceLinks ? (
              <ul className="space-y-2 text-gray-400">
                {serviceLinks.map((link, index) => (
                  <li key={index}>
                    {link.url.startsWith('http') ? (
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-white transition-colors"
                      >
                        {link.text}
                      </a>
                    ) : (
                      <Link to={link.url} className="hover:text-white transition-colors">
                        {link.text}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link
                    to="/cozumler/donanim-destek"
                    className="hover:text-white transition-colors"
                  >
                    Teknik Destek
                  </Link>
                </li>
                <li>
                  <Link
                    to="/cozumler/web-tasarim"
                    className="hover:text-white transition-colors"
                  >
                    Web Tasarım
                  </Link>
                </li>
                <li>
                  <Link
                    to="/cozumler/mobil-uygulama"
                    className="hover:text-white transition-colors"
                  >
                    Mobil Uygulama
                  </Link>
                </li>
              </ul>
            )}
          </div>

          {/* Contact Section */}
          <div>
            {contactContent?.title ? (
              <h4 className="font-semibold mb-4">{contactContent.title}</h4>
            ) : (
              <h4 className="font-semibold mb-4">İletişim</h4>
            )}
            {contactLinks ? (
              <ul className="space-y-2 text-gray-400">
                {contactLinks.map((link, index) => (
                  <li key={index}>
                    {link.url.startsWith('http') || link.url.startsWith('mailto:') || link.url.startsWith('tel:') ? (
                      <a
                        href={link.url}
                        className="hover:text-white transition-colors"
                      >
                        {link.text}
                      </a>
                    ) : (
                      <Link to={link.url} className="hover:text-white transition-colors">
                        {link.text}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="space-y-2 text-gray-400">
                {settings?.email && (
                  <li>
                    Email:{' '}
                    <a
                      href={`mailto:${settings.email}`}
                      className="hover:text-white transition-colors"
                    >
                      {settings.email}
                    </a>
                  </li>
                )}
                {settings?.phone && (
                  <li>
                    Telefon:{' '}
                    <a
                      href={`tel:${settings.phone.replace(/\s/g, '')}`}
                      className="hover:text-white transition-colors"
                    >
                      {settings.phone}
                    </a>
                  </li>
                )}
                {settings?.address && (
                  <li className="text-sm">{settings.address}</li>
                )}
                <li>
                  <Link
                    to="/iletisim"
                    className="hover:text-white transition-colors"
                  >
                    İletişim Formu
                  </Link>
                </li>
              </ul>
            )}
          </div>
        </div>

        <div className="border-t border-primary-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-center md:text-left">
              {copyrightContent?.content || `© ${new Date().getFullYear()} ${settings?.company?.name || import.meta.env.VITE_APP_NAME || 'KUMBİLGİ'}. Tüm hakları saklıdır.`}
            </p>
            
            {/* Social Media Links */}
            {(settings?.social?.facebook || 
              settings?.social?.twitter || 
              settings?.social?.instagram || 
              settings?.social?.linkedin || 
              settings?.social?.youtube || 
              settings?.social?.github) && (
              <div className="flex items-center gap-4">
                {settings?.social?.facebook && (
                  <a
                    href={settings.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                    aria-label="Facebook"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                )}
                {settings?.social?.twitter && (
                  <a
                    href={settings.social.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                    aria-label="Twitter"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                  </a>
                )}
                {settings?.social?.instagram && (
                  <a
                    href={settings.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                    aria-label="Instagram"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                )}
                {settings?.social?.linkedin && (
                  <a
                    href={settings.social.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                    aria-label="LinkedIn"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                )}
                {settings?.social?.youtube && (
                  <a
                    href={settings.social.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                    aria-label="YouTube"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                )}
                {settings?.social?.github && (
                  <a
                    href={settings.social.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                    aria-label="GitHub"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482C19.138 20.197 22 16.425 22 12.017 22 6.484 17.522 2 12 2z" clipRule="evenodd"/>
                    </svg>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}

